import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, getDocs, serverTimestamp
} from 'firebase/firestore';
import ContentData from './ContentData';
import ClientChat from './ClientChat';
import ClarityReportsPanel from './ClarityReportsPanel';
import ChatWidgetPanel from './ChatWidgetPanel';
import ClaudeAccessPanel from './ClaudeAccessPanel';
import DashboardSourcesPanel from './DashboardSourcesPanel';
import DashboardAiPanel from './DashboardAiPanel';
import DashboardLeadsPanel from './DashboardLeadsPanel';
import DashboardUsersPanel from './DashboardUsersPanel';
import DashboardPublishPanel from './DashboardPublishPanel';
import DashboardChatPanel from './DashboardChatPanel';

const ALL_DAYS = ['Su','M','T','W','Th','F','Sa'];
const DAY_LABELS = { Su:'Sun', M:'Mon', T:'Tue', W:'Wed', Th:'Thu', F:'Fri', Sa:'Sat' };

export default function ClientProfile() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);
  const [updates, setUpdates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newUpdate, setNewUpdate] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [contentSettings, setContentSettings] = useState(null);
  const [webflowCollections, setWebflowCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const unsubClient = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setClient(data);
        setContentSettings(cs => cs === null ? {
          clientUrl: data.clientUrl || '',
          wpApiKey: data.wpApiKey || '',
          wpUsername: data.wpUsername || '',
          wpAppPassword: data.wpAppPassword || '',
          publishPlatform: data.publishPlatform || 'wordpress',
          webflowApiToken: data.webflowApiToken || '',
          webflowSiteId: data.webflowSiteId || '6565ff800b30983dc81c001f',
          webflowCollectionId: data.webflowCollectionId || '',
          services: Array.isArray(data.services) ? data.services.map(s => typeof s === 'string' ? s : s.name || s.label || s.type || '').filter(Boolean) : [],
          blogDays: data.blogDays || [],
          imageStyle: data.imageStyle || 'sketch',
          autoPublish: data.autoPublish ?? false,
          firstPersonBlog: data.firstPersonBlog ?? false,
          callscaler_api_key: data.callscaler_api_key || '',
          jobtreadGrantKey: data.jobtreadGrantKey || '',
          clarity_enabled: data.clarity_enabled ?? false,
        } : cs);
      } else {
        setClient(null);
      }
      setLoadingClient(false);
    });

    const unsubClients = onSnapshot(collection(db, 'clients'), snap => {

      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !c.isArchived)
        .sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));
      setClients(all);
    });

    return () => { unsubClient(); unsubClients(); };
  }, [clientId]);

  useEffect(() => {
    const qUpdates = query(collection(db, 'updates'), where('clientId', '==', clientId));
    const unsubUpdates = onSnapshot(qUpdates, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
      setUpdates(data);
    });

    const qTasks = query(collection(db, 'tasks'), where('clientId', '==', clientId));
    const unsubTasks = onSnapshot(qTasks, snap => {
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7); cutoff.setHours(0,0,0,0);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.forEach(t => {
        if (!t.completed) {
          const d = t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate ? new Date(t.dueDate) : null;
          if (d && d < cutoff) deleteDoc(doc(db, 'tasks', t.id));
        }
      });
      setTasks(all.filter(t => {
        if (t.completed) return true;
        const d = t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate ? new Date(t.dueDate) : null;
        return !d || d >= cutoff;
      }));
    });

    return () => { unsubUpdates(); unsubTasks(); };
  }, [clientId]);

  const clientIndex = clients.findIndex(c => c.id === clientId);
  const prevClient = clientIndex > 0 ? clients[clientIndex - 1] : null;
  const nextClient = clientIndex < clients.length - 1 ? clients[clientIndex + 1] : null;

  const postUpdate = async () => {
    if (!newUpdate.trim()) return;
    setPostingUpdate(true);
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, 'updates'), {
        clientId,
        content: newUpdate,
        author: user?.email || 'unknown',
        authorInitials: (user?.email || 'CO').slice(0, 2).toUpperCase(),
        createdAt: serverTimestamp(),
      });
      setNewUpdate('');
    } catch (e) {
      console.error('Failed to post update:', e);
      alert('Failed to post update. Please try again.');
    } finally {
      setPostingUpdate(false);
    }
  };

  const deleteUpdate = async (id) => {
    if (window.confirm('Delete this update?')) {
      await deleteDoc(doc(db, 'updates', id));
    }
  };

  const saveContentSettings = async () => {
    if (!contentSettings) return;
    setSavingSettings(true);
    await updateDoc(doc(db, 'clients', clientId), {
      clientUrl: contentSettings.clientUrl,
      wpApiKey: contentSettings.wpApiKey,
      wpUsername: contentSettings.wpUsername,
      wpAppPassword: contentSettings.wpAppPassword,
      publishPlatform: contentSettings.publishPlatform,
      webflowApiToken: contentSettings.webflowApiToken,
      webflowSiteId: contentSettings.webflowSiteId,
      webflowCollectionId: contentSettings.webflowCollectionId,
      services: contentSettings.services,
      blogDays: contentSettings.blogDays,
      imageStyle: contentSettings.imageStyle,
      autoPublish: contentSettings.autoPublish,
      firstPersonBlog: contentSettings.firstPersonBlog,
      callscaler_api_key: contentSettings.callscaler_api_key,
      jobtreadGrantKey: (contentSettings.jobtreadGrantKey || '').trim(),
      clarity_enabled: contentSettings.clarity_enabled,
    });
    setSavingSettings(false);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  if (loadingClient) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner"></div></div>;
  if (!client) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Client not found.</p>
      <button className="btn btn-secondary" onClick={() => navigate('/clients')}>← Back to Clients</button>
    </div>
  );

  const now = new Date();
  const in30 = new Date(); in30.setDate(now.getDate() + 30);

  const getTaskDate = (t) => {
    if (!t.dueDate) return null;
    return t.dueDate.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
  };

  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);

  const futureTasks = tasks
    .filter(t => { const d = getTaskDate(t); return d && d >= today; })
    .sort((a,b) => getTaskDate(a) - getTaskDate(b));

  const todayTasks = futureTasks.filter(t => getTaskDate(t) < tomorrow);
  const nextDateMs = todayTasks.length === 0 && futureTasks.length > 0
    ? getTaskDate(futureTasks[0]).setHours(0,0,0,0)
    : null;
  const nextDateTasks = nextDateMs !== null
    ? futureTasks.filter(t => { const d = getTaskDate(t); d.setHours(0,0,0,0); return d.getTime() === nextDateMs; })
    : [];
  const contentQueueTasks = todayTasks.length > 0 ? todayTasks : nextDateTasks;
  const contentQueueLabel = todayTasks.length > 0
    ? 'Today'
    : nextDateTasks.length > 0
      ? `Due ${getTaskDate(nextDateTasks[0]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : 'Upcoming';

  const fmtDate = (t) => {
    const d = getTaskDate(t);
    if (!d) return '';
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  const fmtUpdateDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  const updatesCard = (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">Updates</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent-blue-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent-blue)', flexShrink: 0 }}>
          {(auth.currentUser?.email || 'CO').slice(0,2).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <textarea
            placeholder="Write an update..."
            value={newUpdate}
            onChange={e => setNewUpdate(e.target.value)}
            style={{ minHeight: 70, marginBottom: 8 }}
          />
          <div style={{ textAlign: 'right' }}>
            <button className="btn btn-primary btn-sm" onClick={postUpdate} disabled={postingUpdate || !newUpdate.trim()}>
              {postingUpdate ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>

      {updates.map(u => (
        <div key={u.id} style={{ display: 'flex', gap: 10, padding: '12px 0', borderTop: '1px solid var(--border)' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0 }}>
            {(u.authorInitials || u.author?.slice(0,2) || 'CO').toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{u.author || 'content.core6'}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtUpdateDate(u.createdAt)}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{u.content}</div>
          </div>
          <button className="btn btn-sm btn-danger" style={{ alignSelf: 'flex-start', opacity: 0.6 }} onClick={() => deleteUpdate(u.id)}>🗑</button>
        </div>
      ))}
      {updates.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No updates yet</div>}
    </div>
  );

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Top nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/clients')}>← Back to Client List</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" disabled={!prevClient} onClick={() => prevClient && navigate('/clients/' + prevClient.id)}>‹ Prev</button>
          <button className="btn btn-secondary btn-sm" disabled={!nextClient} onClick={() => nextClient && navigate('/clients/' + nextClient.id)}>Next ›</button>
        </div>
      </div>

      {/* Client header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{client.businessName}</h1>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {client.contactName && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>👤 {client.contactName}</span>}
            {client.contactEmail && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>✉ {client.contactEmail}</span>}
            {client.contactPhone && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>📞 {client.contactPhone}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {client.clientUrl && <a href={client.clientUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">Website</a>}
          {client.gptLink && <a href={client.gptLink} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: 'var(--accent-blue-bg)', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)' }}>Claude ↗</a>}
          {client.driveLink && <a href={client.driveLink} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: 'var(--accent-green-bg)', color: 'var(--accent-green)', border: '1px solid var(--accent-green)' }}>Drive ↗</a>}
        </div>
      </div>

      <div className="client-profile-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        {/* Left: Content Data + Content Settings */}
        <div>
          <ContentData clientId={clientId} client={client} />

          {/* Ask Claude — continuous per-client thread + profile audit */}
          <ClientChat clientId={clientId} client={client} />

          {/* Core6 Clarity Reports — latest + archive */}
          <ClarityReportsPanel clientId={clientId} client={client} />

          {/* AI Chat Widget — grounding data + site key for the WP plugin */}
          <ChatWidgetPanel clientId={clientId} client={client} />

          {/* Claude connector — client users who can see only this client */}
          <ClaudeAccessPanel clientId={clientId} client={client} />

          {/* Client dashboard — Windsor ids + tracking-number channel map */}
          <DashboardSourcesPanel clientId={clientId} client={client} />
          <DashboardAiPanel clientId={clientId} client={client} />

          {/* Client dashboard — lead quality review + staff overrides */}
          {client?.dashboardEnabled === true && <DashboardLeadsPanel clientId={clientId} client={client} />}

          {/* Client dashboard — logins, invites, view as client */}
          {client?.dashboardEnabled === true && <DashboardUsersPanel clientId={clientId} client={client} />}

          {/* Client dashboard — review gate: recap, publish, unpublish, auto-publish */}
          {client?.dashboardEnabled === true && <DashboardPublishPanel clientId={clientId} client={client} />}

          {/* Client dashboard — chat transcripts + content request statuses */}
          {client?.dashboardEnabled === true && <DashboardChatPanel clientId={clientId} client={client} />}

          {/* Content Settings (collapsible) */}
          {contentSettings && (
            <div className="card" style={{ marginTop: 20 }}>
              <div
                onClick={() => setSettingsOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
              >
                <div className="card-title" style={{ marginBottom: 0 }}>Content Settings</div>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{settingsOpen ? '▾ Hide' : '▸ Show'}</span>
              </div>
              {settingsOpen && (<div style={{ marginTop: 18 }}>

              <div className="form-field">
                <label>WordPress Site URL</label>
                <input
                  type="url"
                  placeholder="https://www.example.com"
                  value={contentSettings.clientUrl}
                  onChange={e => setContentSettings(s => ({ ...s, clientUrl: e.target.value }))}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Primary site URL — must match the live domain, not staging</div>
              </div>

              <div className="form-field">
                <label>WordPress Publisher API Key</label>
                <input
                  type="text"
                  placeholder="c6pk_..."
                  value={contentSettings.wpApiKey}
                  onChange={e => setContentSettings(s => ({ ...s, wpApiKey: e.target.value }))}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Found in WP Admin → Core6 Publisher</div>
              </div>

              <div className="form-field">
                <label>WP Username</label>
                <input
                  type="text"
                  placeholder="WordPress admin username"
                  value={contentSettings.wpUsername}
                  onChange={e => setContentSettings(s => ({ ...s, wpUsername: e.target.value }))}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>

              <div className="form-field">
                <label>WP Application Password</label>
                <input
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  value={contentSettings.wpAppPassword}
                  onChange={e => setContentSettings(s => ({ ...s, wpAppPassword: e.target.value }))}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Alternative auth — use if plugin key has connectivity issues. Generate in WP Admin → Users → Application Passwords.</div>
              </div>

              {/* Publishing Platform */}
              <div className="form-field">
                <label>Publishing Platform</label>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[
                    { value: 'wordpress', label: 'WordPress', desc: 'Publish via Core6 Publisher plugin or WP REST API' },
                    { value: 'webflow',   label: 'Webflow',   desc: 'Publish directly to a Webflow CMS collection' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setContentSettings(cs => ({ ...cs, publishPlatform: opt.value }))}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', textAlign: 'left', background: contentSettings.publishPlatform === opt.value ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.publishPlatform === opt.value ? '#16a34a55' : 'var(--border)', color: contentSettings.publishPlatform === opt.value ? '#4ade80' : 'var(--text-secondary)' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {contentSettings.publishPlatform === 'webflow' && (
                <>
                  <div className="form-field">
                    <label>Webflow API Token</label>
                    <input
                      type="password"
                      placeholder="Webflow personal access token"
                      value={contentSettings.webflowApiToken}
                      onChange={e => setContentSettings(s => ({ ...s, webflowApiToken: e.target.value }))}
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Found in Webflow → Site Settings → Integrations → API Access</div>
                  </div>
                  <div className="form-field">
                    <label>Webflow Blog Collection ID</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="e.g. 6565ff800b30983dc81c0085"
                        value={contentSettings.webflowCollectionId}
                        onChange={e => setContentSettings(s => ({ ...s, webflowCollectionId: e.target.value }))}
                        style={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={!contentSettings.webflowApiToken || loadingCollections}
                        onClick={async () => {
                          setLoadingCollections(true);
                          try {
                            const idToken = await auth.currentUser?.getIdToken();
                            const res = await fetch('https://us-central1-core6-marketing.cloudfunctions.net/getWebflowCollections', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                              body: JSON.stringify({ webflowApiToken: contentSettings.webflowApiToken }),
                            });
                            const data = await res.json();
                            if (data.error) { alert(data.error); return; }
                            if (data.siteId) setContentSettings(s => ({ ...s, webflowSiteId: data.siteId }));
                            setWebflowCollections(data.collections || []);
                          } catch (e) {
                            alert('Could not fetch collections — check the API token.');
                          } finally {
                            setLoadingCollections(false);
                          }
                        }}
                      >
                        {loadingCollections ? 'Loading…' : 'Browse'}
                      </button>
                    </div>
                    {webflowCollections.length > 0 && (
                      <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                        {webflowCollections.map(col => (
                          <div
                            key={col.id}
                            onClick={() => { setContentSettings(s => ({ ...s, webflowCollectionId: col.id })); setWebflowCollections([]); }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', background: contentSettings.webflowCollectionId === col.id ? '#0a1f12' : 'var(--bg-primary)', color: contentSettings.webflowCollectionId === col.id ? '#4ade80' : 'var(--text-primary)' }}
                          >
                            {col.displayName} <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>{col.id}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>The CMS collection where blog posts are published</div>
                  </div>
                </>
              )}

              <div className="form-field">
                <label>Blog Schedule Days</label>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {ALL_DAYS.map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setContentSettings(cs => ({
                        ...cs,
                        blogDays: cs.blogDays.includes(d) ? cs.blogDays.filter(x => x !== d) : [...cs.blogDays, d]
                      }))}
                      style={{ fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', background: contentSettings.blogDays.includes(d) ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.blogDays.includes(d) ? '#16a34a55' : 'var(--border)', color: contentSettings.blogDays.includes(d) ? '#4ade80' : 'var(--text-muted)' }}
                    >
                      {DAY_LABELS[d]}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>Default publish days shown in Content Queue (Mon & Thu if none set)</div>
              </div>

              <div className="form-field">
                <label>Image Style</label>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[
                    { value: 'illustrated', label: 'Realistic + Watercolor', desc: 'Photo-real subject with hand-painted watercolor surroundings & splatter' },
                    { value: 'watercolor', label: 'Watercolor & Ink', desc: 'Hand-painted watercolor with ink outlines — editorial style' },
                    { value: 'realistic', label: 'Realistic Photo', desc: 'Photorealistic — use with caution for contractors' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setContentSettings(cs => ({ ...cs, imageStyle: opt.value }))}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', textAlign: 'left', background: contentSettings.imageStyle === opt.value ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.imageStyle === opt.value ? '#16a34a55' : 'var(--border)', color: contentSettings.imageStyle === opt.value ? '#4ade80' : 'var(--text-secondary)' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label>Publish Mode</label>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[
                    { value: true,  label: 'Publish',  desc: 'Articles go live immediately when generated' },
                    { value: false, label: 'Draft',    desc: 'Articles saved as drafts — staff must publish manually' },
                  ].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setContentSettings(cs => ({ ...cs, autoPublish: opt.value }))}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', textAlign: 'left', background: contentSettings.autoPublish === opt.value ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.autoPublish === opt.value ? '#16a34a55' : 'var(--border)', color: contentSettings.autoPublish === opt.value ? '#4ade80' : 'var(--text-secondary)' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label>Blog Perspective</label>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[
                    { value: false, label: 'Third Person', desc: 'Standard neutral voice (the business / "we")' },
                    { value: true,  label: 'First Person', desc: 'Reads as the author — personal "I" voice' },
                  ].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setContentSettings(cs => ({ ...cs, firstPersonBlog: opt.value }))}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', textAlign: 'left', background: contentSettings.firstPersonBlog === opt.value ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.firstPersonBlog === opt.value ? '#16a34a55' : 'var(--border)', color: contentSettings.firstPersonBlog === opt.value ? '#4ade80' : 'var(--text-secondary)' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Applies to all of this client's generated blogs.
                </div>
              </div>

              {/* Call Clarity Reports */}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 16px', paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 12 }}>
                  Call Clarity Reports
                </div>

                <div className="form-field">
                  <label>CallScaler API Key</label>
                  <input
                    type="password"
                    placeholder="cs_key_..."
                    value={contentSettings.callscaler_api_key}
                    onChange={e => setContentSettings(s => ({ ...s, callscaler_api_key: e.target.value }))}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Per-client CallScaler token — used server-side only to pull this client's calls.</div>
                </div>

                <div className="form-field">
                  <label>JobTread API Grant Key <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                  <input
                    type="password"
                    placeholder="Only for clients whose website forms go into JobTread"
                    value={contentSettings.jobtreadGrantKey || ''}
                    onChange={e => setContentSettings(s => ({ ...s, jobtreadGrantKey: e.target.value }))}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>When set, the report's website form leads come from JobTread instead of WordPress. Used server-side only; reads new web-form leads (request, city, how they heard), never contact details.</div>
                </div>

                <div className="form-field">
                  <label>Monthly Clarity Report</label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    {[
                      { value: true,  label: 'Enabled',  desc: 'Auto-generate a Clarity PDF on the 1st of each month' },
                      { value: false, label: 'Disabled', desc: 'No automated report for this client' },
                    ].map(opt => (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => setContentSettings(cs => ({ ...cs, clarity_enabled: opt.value }))}
                        style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', textAlign: 'left', background: contentSettings.clarity_enabled === opt.value ? '#0a1f12' : 'var(--bg-primary)', borderColor: contentSettings.clarity_enabled === opt.value ? '#16a34a55' : 'var(--border)', color: contentSettings.clarity_enabled === opt.value ? '#4ade80' : 'var(--text-secondary)' }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                        <div style={{ fontSize: 11, marginTop: 3, opacity: 0.8 }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                <button className="btn btn-primary" onClick={saveContentSettings} disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save Content Settings'}
                </button>
                {settingsSaved && <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>✓ Saved</span>}
              </div>
              </div>)}
            </div>
          )}

        </div>

        {/* Right: Updates + Content Queue */}
        <div>
          {updatesCard}
          {/* Content Queue */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>Content Queue</div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{contentQueueLabel}</span>
            </div>

            {client.gptLink ? (
              <>
                {contentQueueTasks.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0 8px' }}>
                    Nothing upcoming in the queue.
                  </div>
                ) : (
                  contentQueueTasks.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: t.completed ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: t.completed ? 'line-through' : 'none' }}>
                          {t.taskType || t.title}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Due {fmtDate(t)}</div>
                      </div>
                      {t.completed
                        ? <span style={{ fontSize: 11, color: 'var(--accent-green)', fontWeight: 600 }}>✓ Done</span>
                        : <a href={client.gptLink} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: 'var(--accent-blue-bg)', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                            Open in Claude ↗
                          </a>
                      }
                    </div>
                  ))
                )}
                <a href={client.gptLink} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 14, padding: '10px 14px', background: 'var(--accent-blue-bg)', border: '1px solid var(--accent-blue)', borderRadius: 8, color: 'var(--accent-blue)', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                  Open Claude Project ↗
                </a>
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0', lineHeight: 1.6 }}>
                No Claude project linked.<br />
                <span>Add a Claude link in Client Setup to use this panel.</span>
              </div>
            )}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Queue low alerts</span>
              <button
                onClick={async () => {
                  const newVal = !client.noContentQueue;
                  await updateDoc(doc(db, 'clients', clientId), { noContentQueue: newVal });
                  if (newVal) {
                    const snap = await getDocs(query(collection(db, 'tasks'), where('clientId', '==', clientId)));
                    snap.forEach(d => {
                      const type = (d.data().taskType || '').toLowerCase();
                      const title = (d.data().title || '').toLowerCase();
                      if (type.includes('queue low') || type.includes('blog optimization') || title.includes('blog optimization')) {
                        deleteDoc(doc(db, 'tasks', d.id));
                      }
                    });
                  }
                }}
                style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6, border: '1px solid', cursor: 'pointer', background: client.noContentQueue ? '#2d1b1b' : '#0a1f12', borderColor: client.noContentQueue ? '#ef444455' : '#16a34a55', color: client.noContentQueue ? '#f87171' : '#4ade80' }}
              >
                {client.noContentQueue ? 'Opted Out' : 'Active'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
